import dayjs from './dates';
import { InstructorLesson } from '../types';

/**
 * État d'une leçon, dérivé du statut + de l'horaire + de la présence. Remplace l'idée « toute
 * leçon passée est réalisée ».
 *
 * Évaluation de départ, rendez-vous, écoute pédagogique, bilan, vérifications et séance de code
 * (présence présumée, même règle que le serveur `presumed-presence.rules.ts`) ne s'émargent pas :
 * le moniteur signale l'absence de l'élève ; sans absence une heure après la fin, la présence est
 * acquise. `ended` : terminé, absence encore signalable ; `absent` : absence signalée.
 */
export type LessonState = 'cancelled' | 'honored' | 'noshow' | 'upcoming' | 'ended' | 'absent';

const PRESUMED_PRESENCE_TYPES = [
  'Evaluation de départ',
  'Rendez-vous administratif',
  'Rendez-vous préalable',
  'Rendez-vous pédagogique',
  'Ecoute pédagogique',
  'Bilan',
  'Vérifications',
  'Séance de code',
];
/** Délai après la fin pendant lequel l'absence se signale. */
export const ABSENCE_REPORT_DELAY_MS = 60 * 60 * 1000;

/** Le type est libre (reprise d'un ancien planning) : ni casse, ni accents, ni espaces en trop. */
const typeKey = (type: string | null | undefined) =>
  (type ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
const PRESUMED = new Set(PRESUMED_PRESENCE_TYPES.map(typeKey));

/** Évaluation de départ, rendez-vous, bilan… : pas d'émargement, l'absence se signale. */
export function hasPresumedPresence(lesson: { lessonType: string | null }): boolean {
  return PRESUMED.has(typeKey(lesson.lessonType));
}

/** Dernier instant pour signaler l'absence de l'élève. */
export function absenceDeadline(lesson: { endsAt: string }) {
  return dayjs(lesson.endsAt).add(ABSENCE_REPORT_DELAY_MS, 'millisecond');
}

export function lessonState(lesson: {
  status: string;
  endsAt: string;
  honored: boolean;
  lessonType?: string | null;
  absenceReportedAt?: string | null;
}): LessonState {
  if (lesson.status === 'cancelled') return 'cancelled';
  if (hasPresumedPresence({ lessonType: lesson.lessonType ?? null })) {
    if (lesson.absenceReportedAt) return 'absent';
    if (lesson.honored || dayjs().isAfter(absenceDeadline(lesson))) return 'honored';
    return dayjs().isAfter(dayjs(lesson.endsAt)) ? 'ended' : 'upcoming';
  }
  if (lesson.honored) return 'honored';
  if (dayjs().isAfter(dayjs(lesson.endsAt))) return 'noshow';
  return 'upcoming';
}

/** La leçon a commencé. */
export function hasStarted(lesson: { startsAt: string }): boolean {
  return dayjs().isAfter(dayjs(lesson.startsAt));
}

/** L'émargement s'ouvre 15 minutes avant le début (J30, même règle que le serveur `attendance.rules.ts`). */
export const ATTENDANCE_EARLY_MINUTES = 15;

/** L'élève peut signer : la leçon commence dans 15 minutes au plus, ou a commencé. */
export function attendanceOpen(lesson: { startsAt: string }, now = dayjs()): boolean {
  return !now.isBefore(dayjs(lesson.startsAt).subtract(ATTENDANCE_EARLY_MINUTES, 'minute'));
}

/** Émargement possible : dès 15 minutes avant le début, non annulée, non encore honorée, qui s'émarge. */
export function canSignAttendance(lesson: InstructorLesson): boolean {
  return (
    !lesson.isOpenSlot &&
    lesson.status !== 'cancelled' &&
    !lesson.honored &&
    !!lesson.candidateId &&
    !hasPresumedPresence(lesson) &&
    attendanceOpen(lesson)
  );
}

/** Absence signalable : événement à présence présumée commencé, jusqu'à une heure après la fin. */
export function canReportAbsence(lesson: InstructorLesson): boolean {
  return (
    !lesson.isOpenSlot &&
    lesson.status !== 'cancelled' &&
    !!lesson.candidateId &&
    hasPresumedPresence(lesson) &&
    !lesson.absenceReportedAt &&
    !lesson.attendanceSignedAt &&
    hasStarted(lesson) &&
    !dayjs().isAfter(absenceDeadline(lesson))
  );
}
